<template>
  <div class="conteudoContainer">
    <loading :loading="equipamentos === null" />

    <div v-if="equipamentos !== null" class="mapa">
      <md-button
        class="md-icon-button botao-voltar"
        @click="() => $router.back()"
      >
        <md-icon>arrow_back</md-icon>
        <md-tooltip md-direction="right">Voltar</md-tooltip>
      </md-button>

      <equipamento-mapa
        ref="mapa"
        :local-id="localId"
        :equipamentos="equipamentos"
        :equipamentos-com-acao-pendente="equipamentosComAcaoPendente"
        :valores-por-sensor-id="valoresPorSensorId"
        :icones-sensor-visiveis="iconesSensorVisiveis"
        :equipamentos-visiveis="equipamentosVisiveis"
        :camadas="camadas"
        @marker-click="handleMarkerClick"
      />

      <equipamento-mapa-busca
        :local-id="localId"
        :desabilitada="carregandoCamadas > 0"
        @selecionar="focarBusca"
      />

      <equipamento-mapa-camadas
        :camadas="camadas"
        :icones-sensor-visiveis="iconesSensorVisiveis"
        :equipamentos-visiveis="equipamentosVisiveis"
        :carregando="carregandoCamadas > 0"
        :carregar-conteudo="carregarConteudoCamada"
        @adicionar="adicionarCamadas"
        @toggle="toggleCamada"
        @toggle-sensor-icone="toggleSensorIcone"
        @toggle-sensores="toggleSensores"
        @toggle-equipamentos="toggleEquipamentos"
        @renomear="renomearCamada"
        @alterar-cor="alterarCorCamada"
        @substituir="substituirCamada"
        @remover="removerCamada"
        @salvar-padrao="salvarComoPadrao"
        @restaurar="restaurarPadrao"
      />
    </div>

    <slider-right
      id="slideLeft"
      ref="slider"
      size="30%"
      :close-on-click-outside="false"
    >
      <equipamento-mapa-drawer
        v-if="selectedEquipamento !== null"
        :equipamento-id="selectedEquipamento.id"
        :nome="selectedEquipamento.nome"
        :latitude="selectedEquipamento.latitude"
        :longitude="selectedEquipamento.longitude"
        @close="closeSlider"
        @change-acao="fetchPendencias"
      />
    </slider-right>
  </div>
</template>

<script>
import 'mapbox-gl/dist/mapbox-gl.css'

import EquipamentoMapa from '@/components/equipamento/EquipamentoMapa.vue'
import EquipamentoMapaBusca from '@/components/equipamento/EquipamentoMapaBusca.vue'
import EquipamentoMapaCamadas from '@/components/equipamento/EquipamentoMapaCamadas.vue'
import EquipamentoMapaDrawer from '@/components/equipamento/EquipamentoMapaDrawer.vue'
import Loading from '@/components/Loading.vue'
import SliderRight from '@/components/SliderLeft.vue'
import { EventBus, Topics, Types } from '@/event-bus'
import { AcaoStatusTipo } from '@/utils/constants'
import http from '@/utils/http'
import {
  atualizarCamada,
  criarArquivo,
  FORMATO_INP,
  ID_ARQUIVO_EPANET_MODELO,
  listarArquivos,
  obterArquivoGeoJson,
  obterConteudo,
  obterModeloEpanet,
  ORIGEM_EPANET_MODELO,
  removerArquivo,
  renomearArquivo,
  substituirConteudo,
} from '@/utils/mapa-camadas'
import {
  carregarOverrides,
  podarOverrides,
  removerOverride,
  substituirOverride,
} from '@/utils/mapa-camadas-prefs'
import {
  carregarEquipamentosVisiveis,
  carregarIconesSensorVisiveis,
  gravarEquipamentosVisiveis,
  gravarIconesSensorVisiveis,
} from '@/utils/mapa-sensor-icones-prefs'
import { obterIconeSensor, TIPOS_ICONE_MAPA } from '@/utils/sensorMapaIcons'
import { isSensorNoMapa } from '@/utils/sensorMapaWhitelist'
import {
  registerChannelListener,
  removeChannelListener,
} from '@/utils/web-socket'

const POLL_BASE_MS = 20 * 1000
const POLL_JITTER_MS = 10 * 1000

export default {
  name: 'EquipamentoMapaIndex',
  components: {
    EquipamentoMapa,
    EquipamentoMapaBusca,
    EquipamentoMapaCamadas,
    EquipamentoMapaDrawer,
    Loading,
    SliderRight,
  },
  data() {
    return {
      localId: null,
      equipamentos: null,
      equipamentosComAcaoPendente: [],
      selectedEquipamento: null,
      camadas: [],
      // Contador de operações de camada em voo (carga inicial / adicionar /
      // substituir) — dirige a faixa "Carregando…" do painel.
      carregandoCamadas: 0,
      pollTimeout: null,
      destroyed: false,
      valoresPorSensorId: {},
      sensorListeners: [],
      iconesSensorVisiveis: {},
      equipamentosVisiveis: true,
    }
  },
  beforeMount() {
    this.localId = this.$route.params.localId
    this.iconesSensorVisiveis = carregarIconesSensorVisiveis(this.localId)
    this.equipamentosVisiveis = carregarEquipamentosVisiveis(this.localId)

    this.$store.commit('togglePaddingPage')
    this.$store.commit('setToolbarView', false)
  },
  async mounted() {
    document.addEventListener('keydown', this.closeSliderListener)

    await Promise.all([
      this.loadEquipamentos(),
      this.fetchPendencias(),
      this.loadCamadas(),
    ])

    await this.subscribeSensores()
    this.scheduleNextPoll()
  },
  beforeDestroy() {
    this.$store.commit('togglePaddingPage')
    this.$store.commit('setToolbarView', true)

    this.destroyed = true
    this.unsubscribeSensores()
    clearTimeout(this.pollTimeout)
    document.removeEventListener('keydown', this.closeSliderListener)
  },
  methods: {
    async loadEquipamentos() {
      const response = await http.get(`local/${this.localId}/equipamentos`)

      this.equipamentos = (response?.equipamentos ?? [])
        .map(this.normalizarEquipamento)
        .filter(this.isCoordenadaValida)
        .map((equipamento) => ({
          ...equipamento,
          sensores: (equipamento.sensores ?? []).filter(isSensorNoMapa),
        }))
    },
    idsSensoresNoMapa() {
      return (this.equipamentos ?? []).flatMap((equipamento) =>
        (equipamento.sensores ?? []).map((sensor) => String(sensor.id))
      )
    },
    unsubscribeSensores() {
      this.sensorListeners.forEach(({ topic, event, ref }) => {
        try {
          removeChannelListener(topic, event, ref)
        } catch (e) {
          // canal pode não ter sido criado se o join falhou
        }
      })
      this.sensorListeners = []
    },
    async subscribeSensores() {
      this.unsubscribeSensores()
      this.valoresPorSensorId = {}

      if (this.destroyed) return

      await Promise.all(
        this.idsSensoresNoMapa().map((sensorId) =>
          this.subscribeSensor(sensorId)
        )
      )
    },
    async subscribeSensor(sensorId) {
      const topic = `view_sensor_id:${sensorId}`
      const event = 'sensor:update_value'

      const coleta = await http.get(
        `sensor/${sensorId}/ultima-coleta`,
        {},
        false
      )
      if (!this.destroyed) {
        this.aplicarColeta(sensorId, coleta)
      }

      try {
        const ref = await registerChannelListener(topic, event, (payload) => {
          if (this.destroyed) return
          this.aplicarColeta(sensorId, payload)
        })
        this.sensorListeners.push({ topic, event, ref })
      } catch (e) {
        // não impede o GET inicial nem os demais sensores
      }
    },
    normalizarColeta(coleta) {
      if (!coleta || coleta.valor == null) return null

      return {
        valor: coleta.valor,
        horarioColeta: coleta.horarioColeta ?? coleta.horario,
      }
    },
    horarioColetaMs(coleta) {
      if (!coleta || !coleta.horarioColeta) return NaN
      return new Date(coleta.horarioColeta).getTime()
    },
    aplicarColeta(sensorId, coleta) {
      const normalizada = this.normalizarColeta(coleta)
      if (!normalizada) return

      const atual = this.valoresPorSensorId[sensorId]
      const tNovo = this.horarioColetaMs(normalizada)
      const tAtual = this.horarioColetaMs(atual)

      // Coletas atrasadas (fila, avulso antigo, job fora de ordem) não
      // podem substituir um valor mais recente — o Postgres já ignora.
      if (Number.isFinite(tAtual) && Number.isFinite(tNovo) && tNovo < tAtual) {
        return
      }

      this.$set(this.valoresPorSensorId, sensorId, normalizada)
    },
    normalizarEquipamento(equipamento) {
      return {
        ...equipamento,
        latitude: equipamento.latitude ?? equipamento.latitute,
        longitude: equipamento.longitude ?? equipamento.longitute,
      }
    },
    isCoordenadaValida(equipamento) {
      const lat = Number(equipamento.latitude)
      const lng = Number(equipamento.longitude)

      return (
        equipamento.latitude !== null &&
        equipamento.longitude !== null &&
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        !(lat === 0 && lng === 0) &&
        lat !== 11.111111 &&
        lat !== -11.111111 &&
        lat !== 22.222222 &&
        lat !== -22.222222 &&
        lng !== 11.111111 &&
        lng !== -11.111111 &&
        lng !== 22.222222 &&
        lng !== -22.222222 &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
      )
    },
    async fetchPendencias() {
      const response = await http.get(
        `local/${this.localId}/acoes`,
        {
          status: AcaoStatusTipo.Aguardando,
          page: 1,
          pageSize: 200,
        },
        false
      )

      if (!response) return

      this.equipamentosComAcaoPendente = [
        ...new Set(response.acoes.map((acao) => acao.equipamentoId)),
      ]
    },
    scheduleNextPoll() {
      if (this.destroyed) return

      clearTimeout(this.pollTimeout)

      // Jitter para espalhar as requisições entre clientes/abas e evitar
      // rajadas sincronizadas; reagenda só após a resposta, evitando overlap
      const delay = POLL_BASE_MS + Math.random() * POLL_JITTER_MS

      this.pollTimeout = setTimeout(async () => {
        await this.fetchPendencias()
        this.scheduleNextPoll()
      }, delay)
    },
    // ----- Camadas (KML e grupos INP) ----------------------------------------
    // O backend guarda um MapaArquivo por upload; a UI achata em linhas planas
    // (grupo/inp-secao/kml) carregando arquivoId + camadaId para rotear as ações.
    async loadCamadas() {
      this.carregandoCamadas++
      try {
        const [arquivos, modelo] = await Promise.all([
          listarArquivos(this.localId),
          obterModeloEpanet(this.localId),
        ])

        const overrides = carregarOverrides(this.localId)
        const oficiais = this.modeloEpanetParaVms(modelo, overrides)
        const enviados = (arquivos ?? []).flatMap((arquivo) =>
          this.arquivoParaVms(arquivo, overrides)
        )
        this.camadas = [...oficiais, ...enviados]
        // Limpa do localStorage os overrides de camadas que não existem mais
        // (inclui ids estáveis do modelo oficial, senão o prune os apaga).
        podarOverrides(
          this.localId,
          this.camadas.filter((c) => c.camadaId != null).map((c) => c.camadaId)
        )
        await Promise.all(
          (arquivos ?? []).map((arquivo) =>
            this.carregarGeoJsonArquivo(arquivo.id)
          )
        )
      } finally {
        this.carregandoCamadas--
      }
    },
    ehModeloEpanet(camada) {
      return camada?.origem === ORIGEM_EPANET_MODELO
    },
    // Espelho do ModeloInp: mesmo VM de grupo + seções de um INP importado,
    // com ids estáveis (epanet-modelo / epanet-modelo:chave) e GeoJSON já no
    // payload. Sem modelo (null) não cria linha; parse inválido cria só o grupo
    // com erro.
    modeloEpanetParaVms(modelo, overrides = {}) {
      if (!modelo) {
        return []
      }

      const arquivoId = ID_ARQUIVO_EPANET_MODELO
      const secoes = modelo.secoes ?? []
      const filhas = secoes.map((c) => {
        const id = `${arquivoId}:${c.chave}`
        const o = overrides[id] || null
        const gj = c.geoJson

        return {
          id,
          tipo: 'inp-secao',
          origem: ORIGEM_EPANET_MODELO,
          arquivoId,
          camadaId: id,
          parentId: arquivoId,
          chave: c.chave,
          nome: c.nome,
          fileName: null,
          geojson: gj ? Object.freeze(gj) : null,
          versao: gj ? 1 : 0,
          erro: gj ? null : 'sem geometria',
          corDefault: c.cor,
          visivelDefault: c.visivel,
          cor: o && o.cor != null ? o.cor : c.cor,
          visivel: o && o.visivel != null ? o.visivel : c.visivel,
          temOverride: !!o,
        }
      })

      return [
        {
          id: arquivoId,
          tipo: 'grupo',
          origem: ORIGEM_EPANET_MODELO,
          arquivoId,
          camadaId: null,
          parentId: null,
          chave: null,
          nome: modelo.nome || 'Modelo EPANET',
          fileName: null,
          tamanhoBytes: 0,
          cor: null,
          corDefault: null,
          visivel: filhas.some((f) => f.visivel),
          visivelDefault: null,
          temOverride: false,
          geojson: null,
          versao: 0,
          erro: modelo.erro || null,
        },
        ...filhas,
      ]
    },
    // Um arquivo INP vira grupo-pai + N filhas (uma por seção). Um KML vira uma
    // linha só, que exibe o NOME DO ARQUIVO (não o da camada). `overrides` são os
    // ajustes locais do usuário (localStorage): `cor`/`visivel` exibidos = override
    // se houver, senão o default do banco (guardado em `corDefault`/`visivelDefault`).
    arquivoParaVms(arquivo, overrides = {}) {
      const camadas = arquivo.camadas ?? []

      // Monta cor/visivel efetivos + default + flag de override para uma camada.
      const efetivo = (c) => {
        const o = overrides[c.id] || null
        return {
          corDefault: c.cor,
          visivelDefault: c.visivel,
          cor: o && o.cor != null ? o.cor : c.cor,
          visivel: o && o.visivel != null ? o.visivel : c.visivel,
          temOverride: !!o,
        }
      }

      if (arquivo.formato === FORMATO_INP) {
        const filhas = camadas.map((c) => ({
          id: c.id,
          tipo: 'inp-secao',
          origem: null,
          arquivoId: arquivo.id,
          camadaId: c.id,
          parentId: arquivo.id,
          chave: c.chave,
          nome: c.nome,
          fileName: arquivo.fileName,
          geojson: null,
          versao: 0,
          erro: null,
          ...efetivo(c),
        }))
        const grupo = {
          id: arquivo.id,
          tipo: 'grupo',
          origem: null,
          arquivoId: arquivo.id,
          camadaId: null,
          parentId: null,
          chave: null,
          nome: arquivo.nome,
          fileName: arquivo.fileName,
          tamanhoBytes: arquivo.tamanhoBytes,
          cor: null,
          corDefault: null,
          visivel: filhas.some((f) => f.visivel),
          visivelDefault: null,
          temOverride: false,
          geojson: null,
          versao: 0,
          erro: null,
        }
        return [grupo, ...filhas]
      }

      const c = camadas[0] ?? {}
      return [
        {
          id: c.id,
          tipo: 'kml',
          origem: null,
          arquivoId: arquivo.id,
          camadaId: c.id,
          parentId: null,
          chave: null,
          nome: arquivo.nome,
          fileName: arquivo.fileName,
          tamanhoBytes: arquivo.tamanhoBytes,
          geojson: null,
          versao: 0,
          erro: null,
          ...efetivo(c),
        },
      ]
    },
    // Baixa o GeoJSON do arquivo (parseado no backend) e distribui às linhas por
    // chave: filha INP pela sua seção; KML pela única entrada (chave null).
    async carregarGeoJsonArquivo(arquivoId) {
      const secoes = await obterArquivoGeoJson(this.localId, arquivoId)
      const linhas = this.camadas.filter(
        (c) => c.arquivoId === arquivoId && c.tipo !== 'grupo'
      )

      if (!secoes) {
        linhas.forEach((c) => {
          c.geojson = null
          c.erro = 'falha ao carregar geometria'
        })
        return
      }

      const porChave = new Map(secoes.map((s) => [s.chave ?? null, s.geoJson]))
      linhas.forEach((c) => {
        // Object.freeze evita o Vue reativar recursivamente um GeoJSON grande.
        const gj = porChave.get(c.chave ?? null)
        if (gj) {
          c.geojson = Object.freeze(gj)
          c.versao += 1
          c.erro = null
        } else {
          c.geojson = null
          c.erro = 'sem geometria'
        }
      })
    },
    carregarConteudoCamada(id) {
      const camada = this.camadas.find((c) => c.id === id)
      if (!camada || this.ehModeloEpanet(camada)) return Promise.resolve('')
      return obterConteudo(this.localId, camada.arquivoId)
    },
    async adicionarCamadas(arquivos) {
      this.carregandoCamadas++
      try {
        for (const arquivo of arquivos) {
          const arquivoId = await criarArquivo(this.localId, arquivo)
          if (!arquivoId) continue
          await this.carregarArquivo(arquivoId, false)
        }
      } finally {
        this.carregandoCamadas--
      }
    },
    // (Re)carrega um arquivo pela lista: opcionalmente remove as linhas antigas
    // dele e recria a partir do backend (metadados + geojson). Usado ao criar e
    // ao substituir (a reconciliação de seções INP acontece no backend).
    async carregarArquivo(arquivoId, removerAntigas = true) {
      const arquivos = await listarArquivos(this.localId)
      const arquivo = arquivos?.find((a) => a.id === arquivoId)

      if (removerAntigas) {
        this.camadas = this.camadas.filter((c) => c.arquivoId !== arquivoId)
      }
      if (!arquivo) return

      const overrides = carregarOverrides(this.localId)
      this.camadas.push(...this.arquivoParaVms(arquivo, overrides))
      await this.carregarGeoJsonArquivo(arquivoId)
    },
    // Recalcula o override local da camada a partir do efetivo atual x default:
    // grava só os campos que DIVERGEM; se nada difere, remove o override e apaga
    // a flag. É isso que faz a flag sumir quando o usuário volta cor/visibilidade
    // ao default "na mão" (sem usar o botão Voltar ao padrão).
    sincronizarOverride(camada) {
      const override = {}
      const corDifere =
        (camada.cor || '').toLowerCase() !==
        (camada.corDefault || '').toLowerCase()
      if (corDifere) override.cor = camada.cor
      if (camada.visivel !== camada.visivelDefault) {
        override.visivel = camada.visivel
      }

      camada.temOverride = Object.keys(override).length > 0
      substituirOverride(this.localId, camada.camadaId, override)
    },
    // Cor: SEMPRE override local (todos os papéis). O default no banco só muda
    // via "Salvar como padrão". O mapa repinta pelo watch de `camadas`.
    alterarCorCamada({ id, cor }) {
      const camada = this.camadas.find((c) => c.id === id)
      if (!camada) return

      camada.cor = cor
      this.sincronizarOverride(camada)
    },
    persistirIconesSensor() {
      gravarIconesSensorVisiveis(this.localId, this.iconesSensorVisiveis)
    },
    persistirEquipamentos() {
      gravarEquipamentosVisiveis(this.localId, this.equipamentosVisiveis)
    },
    toggleEquipamentos() {
      this.equipamentosVisiveis = !this.equipamentosVisiveis
      this.persistirEquipamentos()
    },
    toggleSensorIcone(id) {
      this.$set(
        this.iconesSensorVisiveis,
        id,
        this.iconesSensorVisiveis[id] !== true
      )
      this.persistirIconesSensor()
    },
    toggleSensores() {
      const algumVisivel = TIPOS_ICONE_MAPA.some(
        (tipo) => this.iconesSensorVisiveis[tipo.id] === true
      )
      TIPOS_ICONE_MAPA.forEach((tipo) => {
        this.$set(this.iconesSensorVisiveis, tipo.id, !algumVisivel)
      })
      this.persistirIconesSensor()
    },
    // Visibilidade: SEMPRE override local (todos os papéis). Nunca persiste aqui.
    toggleCamada(id) {
      const camada = this.camadas.find((c) => c.id === id)
      if (!camada) return

      // Olho do grupo = mestre: se há alguma filha visível, esconde todas; senão
      // mostra todas. O grupo não tem persistência própria — reflete as filhas.
      if (camada.tipo === 'grupo') {
        const filhas = this.camadas.filter((c) => c.parentId === id)
        const visivel = !filhas.some((f) => f.visivel)
        camada.visivel = visivel
        filhas.forEach((f) => {
          f.visivel = visivel
          this.sincronizarOverride(f)
        })
        return
      }

      const visivel = !camada.visivel
      camada.visivel = visivel
      this.sincronizarOverride(camada)
    },
    // Promove o estado local atual (cor+visibilidade) a default no banco — só
    // editor (o kebab que dispara isto já é gated por permissão; o backend
    // reforça com MapaEdicao). Para grupo INP, persiste todas as filhas. Ao
    // salvar, o default passa a valer o efetivo e o override local é descartado.
    async salvarComoPadrao(id) {
      const camada = this.camadas.find((c) => c.id === id)
      if (!camada || this.ehModeloEpanet(camada)) return

      const alvos =
        camada.tipo === 'grupo'
          ? this.camadas.filter((c) => c.parentId === id)
          : [camada]
      if (alvos.length === 0) return

      this.carregandoCamadas++
      try {
        await Promise.all(
          alvos.map((c) =>
            atualizarCamada(this.localId, c.camadaId, {
              cor: c.cor,
              visivel: c.visivel,
            })
          )
        )
        alvos.forEach((c) => {
          c.corDefault = c.cor
          c.visivelDefault = c.visivel
          c.temOverride = false
          removerOverride(this.localId, c.camadaId)
        })
        if (camada.tipo === 'grupo') camada.temOverride = false

        EventBus.$emit(Topics.NOTIFICATIONS, {
          type: Types.INFO,
          message: 'Visualização salva como padrão',
        })
      } finally {
        this.carregandoCamadas--
      }
    },
    // Descarta o override local (todos os papéis): cor/visibilidade voltam ao
    // default do banco. Para grupo INP, restaura todas as filhas.
    restaurarPadrao(id) {
      const camada = this.camadas.find((c) => c.id === id)
      if (!camada) return

      const alvos =
        camada.tipo === 'grupo'
          ? this.camadas.filter((c) => c.parentId === id)
          : [camada]

      alvos.forEach((c) => {
        c.cor = c.corDefault
        c.visivel = c.visivelDefault
        c.temOverride = false
        removerOverride(this.localId, c.camadaId)
      })
      if (camada.tipo === 'grupo') camada.temOverride = false
    },
    async renomearCamada({ id, nome }) {
      const camada = this.camadas.find((c) => c.id === id)
      if (!camada || this.ehModeloEpanet(camada)) return

      camada.nome = nome
      await renomearArquivo(this.localId, camada.arquivoId, nome)
    },
    async substituirCamada({ id, fileName, conteudo }) {
      const camada = this.camadas.find((c) => c.id === id)
      if (!camada || this.ehModeloEpanet(camada)) return

      this.carregandoCamadas++
      try {
        const arquivoId = await substituirConteudo(
          this.localId,
          camada.arquivoId,
          {
            conteudo,
            fileName,
          }
        )
        if (!arquivoId) return

        // Usa o id retornado pelo backend (fonte da verdade), não camada.arquivoId
        // — hoje são iguais, mas evita dessincronia se o backend mudar o id.
        await this.carregarArquivo(arquivoId)
      } finally {
        this.carregandoCamadas--
      }
    },
    async removerCamada(id) {
      const camada = this.camadas.find((c) => c.id === id)
      if (!camada || this.ehModeloEpanet(camada)) return

      this.carregandoCamadas++
      try {
        const ok = await removerArquivo(this.localId, camada.arquivoId)
        if (!ok) return

        // Remover um arquivo tira junto todas as suas linhas (grupo + filhas).
        this.camadas = this.camadas.filter(
          (c) => c.arquivoId !== camada.arquivoId
        )
      } finally {
        this.carregandoCamadas--
      }
    },

    focarBusca(item) {
      if (!item) return

      if (item.tipo === 'equipamento' || item.tipo === 'sensor') {
        this.garantirAlvoSpiderVisivel(item)
      }

      if (item.camadaId) {
        const camada = this.camadas.find(
          (c) => String(c.id) === String(item.camadaId)
        )
        if (camada && !camada.visivel) {
          camada.visivel = true
          this.sincronizarOverride(camada)
        }
      }

      const alvo = this.resolverAlvoBusca(item)
      this.$nextTick(() => {
        this.$refs.mapa?.focarAlvo(alvo)
      })
    },
    garantirAlvoSpiderVisivel(item) {
      if (!this.equipamentosVisiveis) {
        this.equipamentosVisiveis = true
        this.persistirEquipamentos()
      }

      if (item.tipo !== 'sensor' || item.sensorId == null) {
        return
      }

      let sensor = null
      ;(this.equipamentos || []).some((equipamento) => {
        sensor = (equipamento.sensores || []).find(
          (s) => String(s.id) === String(item.sensorId)
        )
        return !!sensor
      })
      if (!sensor) {
        return
      }

      const icone = obterIconeSensor(sensor.tipoNome)
      if (!icone || this.iconesSensorVisiveis[icone] === true) {
        return
      }

      this.$set(this.iconesSensorVisiveis, icone, true)
      this.persistirIconesSensor()
    },
    resolverAlvoBusca(item) {
      const alvo = {
        lng: item.lng,
        lat: item.lat,
        titulo: item.titulo,
        geometry: null,
        properties: null,
        chave: null,
        camadaId: item.camadaId || null,
        sensorId: item.sensorId,
        equipamentoId: item.equipamentoId,
      }
      if (!item.camadaId) {
        return alvo
      }

      const camada = this.camadas.find(
        (c) => String(c.id) === String(item.camadaId)
      )
      if (camada) {
        alvo.chave = camada.chave || null
      }
      const features = camada && camada.geojson && camada.geojson.features
      if (!features || !features.length) {
        return alvo
      }

      const iguais = features.filter((f) => {
        const props = f.properties || {}
        const nome = props.nome != null ? props.nome : props.Nome
        return nome != null && String(nome) === String(item.titulo)
      })
      if (!iguais.length) {
        return alvo
      }

      const escolhida =
        iguais.length === 1
          ? iguais[0]
          : this.feicaoMaisProxima(iguais, item.lng, item.lat)
      if (escolhida && escolhida.geometry) {
        alvo.geometry = escolhida.geometry
        alvo.properties = escolhida.properties || null
      }
      return alvo
    },
    feicaoMaisProxima(features, lng, lat) {
      let melhor = features[0]
      let menor = Infinity
      features.forEach((f) => {
        const p = this.primeiroPontoGeo(f.geometry)
        if (!p) {
          return
        }
        const d = (p[0] - lng) * (p[0] - lng) + (p[1] - lat) * (p[1] - lat)
        if (d < menor) {
          menor = d
          melhor = f
        }
      })
      return melhor
    },
    primeiroPontoGeo(geometry) {
      let node = geometry && geometry.coordinates
      while (node && typeof node[0] !== 'number') {
        node = node[0]
      }
      return node && typeof node[0] === 'number' ? node : null
    },
    handleMarkerClick(equipamento) {
      this.selectedEquipamento = equipamento

      setTimeout(() => {
        if (this.$refs.slider.isOpened() === false) {
          this.$refs.slider.toogleTab()
        }
      }, 0)
    },
    closeSlider() {
      this.$refs.slider.close()
      this.selectedEquipamento = null
    },
    closeSliderListener(event) {
      if (event.key === 'Escape') {
        this.closeSlider()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.conteudoContainer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.mapa {
  height: 100%;
}

.botao-voltar {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 6;
  margin: 0;
  width: 44px;
  min-width: 44px;
  height: 44px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

  ::v-deep .md-icon {
    color: #333;
  }

  // O md-icon-button desenha DUAS camadas circulares (border-radius 50%): o
  // .md-ripple (clique) e o ::before (estado de hover). Num botão quadrado-
  // arredondado ambos viram um círculo estranho — alinha os dois ao raio.
  ::v-deep .md-ripple {
    border-radius: 6px;
  }

  &::before {
    border-radius: 6px;
  }
}
</style>
